import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FiChevronDown } from 'react-icons/fi';

export interface BroadcastWhatsAppGroup {
  id: number;
  name: string;
  participantCount?: number;
  participant_count?: number;
}

function memberCount(g: BroadcastWhatsAppGroup): number {
  return g.participant_count ?? g.participantCount ?? 0;
}

const GROUP_OPTION_HEIGHT = 40;
const GROUP_LIST_MAX_HEIGHT = 220;

export interface VirtualizedBroadcastGroupPickerProps {
  groups: BroadcastWhatsAppGroup[];
  value: number | null;
  onChange: (groupId: number | null) => void;
  disabled?: boolean;
  /** When true, user must pick one group (no “all contacts” option). */
  requireGroup?: boolean;
  placeholder?: string;
  allContactsLabel?: string;
  hintId?: string;
}

export const VirtualizedBroadcastGroupPicker: React.FC<VirtualizedBroadcastGroupPickerProps> = ({
  groups,
  value,
  onChange,
  disabled = false,
  requireGroup = false,
  placeholder = 'Selecione um grupo…',
  allContactsLabel = 'Todos os contatos',
  hintId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  const listHeight = Math.min(GROUP_LIST_MAX_HEIGHT, Math.max(GROUP_OPTION_HEIGHT, filtered.length * GROUP_OPTION_HEIGHT));

  const selected = value != null ? groups.find((g) => g.id === value) : null;
  const summary =
    selected != null
      ? `${selected.name} (${memberCount(selected)} membros)`
      : requireGroup
        ? placeholder
        : allContactsLabel;

  return (
    <div
      ref={rootRef}
      className={`broadcast-group-picker ${open ? 'broadcast-group-picker--open' : ''}`}
    >
      <button
        type="button"
        className="broadcast-group-picker__trigger form-select"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-describedby={hintId}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className="broadcast-group-picker__summary">{summary}</span>
        <FiChevronDown className="broadcast-group-picker__chevron" size={18} aria-hidden />
      </button>
      {open && (
        <div className="broadcast-group-picker__panel" role="listbox">
          <input
            type="text"
            className="broadcast-group-picker__search"
            placeholder="Buscar grupos…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <Virtuoso
            style={{ height: listHeight, width: '100%' }}
            data={filtered}
            computeItemKey={(_i, g) => String(g.id)}
            itemContent={(_index, g) => {
              const active = value === g.id;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`broadcast-group-picker__option${active ? ' broadcast-group-picker__option--active' : ''}`}
                  onClick={() => {
                    onChange(g.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  {g.name} ({memberCount(g)} membros)
                </button>
              );
            }}
          />
          {!requireGroup && (
            <button
              type="button"
              className="broadcast-group-picker__all"
              onClick={() => {
                onChange(null);
                setOpen(false);
                setSearch('');
              }}
            >
              {allContactsLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
